import { gql, useMutation, useQuery } from '@apollo/client';
import IconInfoSolid from '@apollo/icons/default/IconInfoSolid.svg';
import {
  Alert,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
  customColors,
} from '@apollo/orbit';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { FlatDiffItemFragment } from 'src/app/graph/proposalLayout/diffHelpers/diffHelpers';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { asyncRerunCheckWorkflowMutation } from '../../summaryRerunButton/SummaryRerunButton';
import { DefaultOverrideViolationButton } from '../lintCheckDetails/overrideViolationButton/defaultOverrideViolationButton/DefaultOverrideViolationButton';
import { ConfirmationModal } from '../operationCheckResults/operationCheckDetails/overrideButton/confirmationModal/ConfirmationModal';

import { NewProposalsCheckBehaviourAlert } from './NewProposalsCheckBehaviourAlert';
import {
  ProposalCheckDetailsDiffDisplay,
  ProposalCheckDetailsDiffDisplayFlatDiffItemFragment,
} from './proposalCheckDetailsDiffDisplay/ProposalCheckDetailsDiffDisplay';

const { bg, border } = customColors;

const ProposalCheckDetailsWorkflowFragment = gql`
  fragment ProposalCheckDetailsWorkflowFragment on CheckWorkflow {
    id
    subgraphNames
    baseSubgraphs {
      hash
      name
    }
    proposedSubgraphs {
      hash
      name
    }
  }
`;
const ProposalCheckDetailsTaskFragment = gql`
  fragment ProposalCheckDetailsTaskFragment on CheckWorkflowTask {
    id
    ... on ProposalsCheckTask {
      didOverrideProposalsCheckTask
      hasExceededMaxDiffs
      severityLevel
      diffs {
        ...ProposalCheckDetailsDiffDisplayFlatDiffItemFragment
      }
    }
  }
  ${FlatDiffItemFragment}
  ${ProposalCheckDetailsDiffDisplayFlatDiffItemFragment}
`;

export function ProposalCheckDetails() {
  const { currentProposalCheckDetailsRoute } = useInternalGraphLinking();
  const { checkId, graphId } = useRouteParams(currentProposalCheckDetailsRoute);
  const { data, loading, error } = useQuery<
    GraphQLTypes.ProposalCheckDetailsQuery,
    GraphQLTypes.ProposalCheckDetailsQueryVariables
  >(
    gql`
      query ProposalCheckDetailsQuery($graphId: ID!, $checkId: ID!) {
        service(id: $graphId) {
          id
          checkWorkflow(id: $checkId) {
            id
            ...ProposalCheckDetailsWorkflowFragment
            tasks {
              id
              ...ProposalCheckDetailsTaskFragment
            }
          }
        }
      }
      ${ProposalCheckDetailsTaskFragment}
      ${ProposalCheckDetailsWorkflowFragment}
    `,
    {
      variables: { graphId, checkId },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );
  const workflow = data?.service?.checkWorkflow;
  const workflowTask = workflow?.tasks.find(
    (task) => task.__typename === 'ProposalsCheckTask',
  );

  const [
    updateOverrideProposalsCheckTask,
    { loading: updateLoading, error: updateError },
  ] = useMutation<
    GraphQLTypes.OverrideProposalsCheckTaskMutation,
    GraphQLTypes.OverrideProposalsCheckTaskMutationVariables
  >(gql`
    mutation OverrideProposalsCheckTaskMutation(
      $taskId: ID!
      $graphId: ID!
      $shouldOverride: Boolean!
    ) {
      graph(id: $graphId) {
        overrideProposalsCheckTask(
          taskId: $taskId
          shouldOverride: $shouldOverride
        )
      }
    }
  `);

  const [rerunCheck, { loading: rerunLoading, error: rerunError }] =
    useMutation(asyncRerunCheckWorkflowMutation);

  const [isIgnoreModalOpen, setIgnoreModalOpen] = useState<boolean>(false);

  const submitOverride = async (shouldOverride: boolean, taskId: string) => {
    await updateOverrideProposalsCheckTask({
      variables: { graphId, taskId, shouldOverride },
      update: (cache) => {
        cache.evict({
          id: cache.identify({
            __typename: 'ProposalsCheckTask',
            id: taskId,
          }),
          fieldName: 'didOverrideProposalsCheckTask',
        });
      },
    });

    await rerunCheck({
      variables: {
        graphId,
        checkWorkflowId: checkId,
      },
      onCompleted: (rerunData) => {
        const result = rerunData.service?.checkWorkflow?.rerunAsync;
        if (
          result?.__typename === 'InvalidInputError' ||
          result?.__typename === 'PermissionError' ||
          result?.__typename === 'PlanError'
        ) {
          Sentry.captureMessage(result.message, { level: 'error' });
        }
      },
    });
  };

  const subgraphNames = workflow?.subgraphNames;
  const isOverridden =
    workflowTask?.__typename === 'ProposalsCheckTask' &&
    workflowTask.didOverrideProposalsCheckTask;

  const schemaHashesBySubgraph = useMemo(
    () =>
      Object.fromEntries(
        subgraphNames?.map((subgraphName) => [
          subgraphName,
          {
            baseSchemaHash: workflow?.baseSubgraphs?.find(
              (subgraph) => subgraph.name === subgraphName,
            )?.hash,
            proposedSchemaHash: workflow?.proposedSubgraphs?.find(
              (subgraph) => subgraph.name === subgraphName,
            )?.hash,
          },
        ]) ?? [],
      ),
    [subgraphNames, workflow],
  );

  const [
    hasScrolledToFirstFailedCoordinateOrLinked,
    setHasScrolledToFirstFailedCoordinateOrLinked,
  ] = useState(false);

  const diffItemsBySubgraph = useMemo(() => {
    const diffItems =
      workflowTask?.__typename === 'ProposalsCheckTask'
        ? workflowTask.diffs
        : [];
    return _.groupBy(diffItems, ({ subgraph }) => subgraph);
  }, [workflowTask]);

  // Split subgraphs into those with all approved proposals and those without and sort them alphabetically
  const [subgraphsWithApprovedProposals, subgraphsWithoutApprovedProposals] =
    useMemo(() => {
      return _.partition(Object.entries(diffItemsBySubgraph), ([, diffItems]) =>
        diffItems.every((diffItem) => diffItem.hasApprovedProposal),
      ).map((subgraphEntries) =>
        _.orderBy(subgraphEntries, [([subgraphName]) => subgraphName], ['asc']),
      );
    }, [diffItemsBySubgraph]);

  if (loading) {
    return <Loading />;
  } else if (error) {
    return <ErrorMessage error={error} />;
  } else if (
    workflowTask?.__typename !== 'ProposalsCheckTask' ||
    !subgraphNames ||
    subgraphNames?.length === 0
  ) {
    return <NotFound />;
  } else if (workflowTask.hasExceededMaxDiffs) {
    return (
      <CardBody>
        <Alert status="error">
          The total number of schema changes in this check exceeds the maximum
          limit for proposal checks.
        </Alert>
      </CardBody>
    );
  }

  const isIgnored = workflowTask.didOverrideProposalsCheckTask;

  return (
    <>
      <style>
        {`
          .xxx-monaco-highlight-line {
            background-color: ${bg.highlight};
            border-top: 1px dashed ${border.highlight};
            border-bottom: 1px dashed ${border.highlight};
            z-index: -1;
          }

          /* This is the only way to temporarily remove the highlighting of a selected line.
           It should work on most modern browsers but is not fully guaranteed (not added for firefox yet) */
          .line-insert:has(~ .xxx-monaco-highlight-line),
          .char-insert:has(~ .xxx-monaco-highlight-line),
          .line-delete:has(~ .xxx-monaco-highlight-line),
          .char-delete:has(~ .xxx-monaco-highlight-line) {
            display: none;
          }

          /* TODO (jason) fix this upstream */
          /* The monaco glyph hover css is broken, add some size/padding */
          .monaco-editor-hover
            .hover-row
            > div:not(.hover-contents):not(.actions) {
            font-size: 12px;
            padding-top: 4px;
            padding-bottom: 4px;
            padding-left: 8px;
            padding-right: 8px;
            font-family: "var(--font-mono)";
          }
          .monaco-editor-hover .hover-row .hover-contents p {
            padding-top: 8px;
          }
        `}
      </style>
      <CardHeader>
        <CardTitle>Proposals</CardTitle>
        <DefaultOverrideViolationButton
          graphId={graphId}
          buttonText={isOverridden ? 'Unignore' : 'Ignore'}
          onClick={() => setIgnoreModalOpen(true)}
          loading={updateLoading}
        />
        {isIgnored && (
          <CardDescription>
            <IconInfoSolid className="mr-2 mt-0.5 text-icon-primary" />
            This task is currently ignoring the status of its matching proposals
            and will always show up as successful. To make sure all schema
            changes are tied to an{' '}
            <span className="font-semibold">approved</span> proposal, select
            “Unignore”.
          </CardDescription>
        )}
      </CardHeader>
      <NewProposalsCheckBehaviourAlert />
      <CardBody className="overflow-y-auto">
        {subgraphsWithoutApprovedProposals?.map(
          ([subgraphName, diffItems], i) => (
            <ProposalCheckDetailsDiffDisplay
              key={subgraphName}
              severityLevel={workflowTask.severityLevel}
              subgraphName={subgraphName}
              proposedSchemaHash={
                schemaHashesBySubgraph[subgraphName]?.proposedSchemaHash
              }
              baseSchemaHash={
                schemaHashesBySubgraph[subgraphName]?.baseSchemaHash
              }
              diffItems={diffItems ?? []}
              shouldScrollToFirstFailedCoordinateIfPossible={
                !hasScrolledToFirstFailedCoordinateOrLinked && i === 0
              }
              setHasScrolledToFirstFailedCoordinateOrLinked={
                setHasScrolledToFirstFailedCoordinateOrLinked
              }
            />
          ),
        )}
        {subgraphsWithApprovedProposals?.map(([subgraphName, diffItems]) => (
          <ProposalCheckDetailsDiffDisplay
            key={subgraphName}
            severityLevel={workflowTask.severityLevel}
            subgraphName={subgraphName}
            proposedSchemaHash={
              schemaHashesBySubgraph[subgraphName]?.proposedSchemaHash
            }
            baseSchemaHash={
              schemaHashesBySubgraph[subgraphName]?.baseSchemaHash
            }
            diffItems={diffItems ?? []}
            shouldScrollToFirstFailedCoordinateIfPossible={false}
            setHasScrolledToFirstFailedCoordinateOrLinked={
              setHasScrolledToFirstFailedCoordinateOrLinked
            }
          />
        ))}
        {!subgraphsWithApprovedProposals &&
          !subgraphsWithoutApprovedProposals && (
            <span className="text-base">
              This check has no schema changes to display.
            </span>
          )}
      </CardBody>
      <ConfirmationModal
        isOpen={isIgnoreModalOpen}
        title={`${
          isOverridden ? 'Unignore' : 'Ignore'
        } this proposal check result`}
        description={
          <>
            {isOverridden
              ? 'By selecting this action, you acknowledge that the status of this proposal will cause your checks to warn or fail when there are discrepancies between your proposal and the changes being pushed through the pipeline.'
              : 'By selecting this action, you acknowledge that the status of this proposal will no longer cause your checks to warn or fail, even if there are discrepancies between your proposal and the changes being pushed through the pipeline.'}
            <Alert status="info">
              <span className="font-semibold">To apply the changes,</span>{' '}
              <span>a rerun of this check will be started.</span>
            </Alert>
            {(rerunLoading || updateLoading) && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-secondary opacity-75">
                <Spinner size="sm" />
              </div>
            )}
            {(rerunError || updateError) && <ErrorMessage />}
          </>
        }
        onSubmit={() => {
          submitOverride(!isOverridden, workflowTask.id);
        }}
        onClose={() => {
          setIgnoreModalOpen(false);
        }}
      />
    </>
  );
}
